/**
 * Derived measurements for Review — reads existing IR components (no backend change).
 * Scale from bar-scale / ratio text; dimensions from component value strings.
 */

const SCALE_RATIO_RE = /(\d+)\s*[:=]\s*(\d+)/i;
const NTS_RE = /^(?:n\.?t\.?s\.?|not\s+to\s+scale)$/i;
const ARCH_DIM_RE = /(\d+)\s*['′]\s*-?\s*(\d+(?:\s+\d+\/\d+)?)?\s*["″]?/;
const FEET_ONLY_RE = /^(\d+(?:\.\d+)?)\s*['′]\s*$/;
const INCH_ONLY_RE = /^(\d+(?:\s+\d+\/\d+)?|\d+\.\d+)\s*["″]$/;
const AREA_SF_RE = /(\d+(?:\.\d+)?)\s*(?:sf|s\.?f\.?|sq\.?\s*ft)/i;

const BAR_SCALE_KIND = 'bar scale';
const SCALE_KINDS = new Set([
  'bar scale',
  'graphic scale',
  'scale notation',
  'sheet scale notation',
  'drawing scale',
]);
const SCALE_HINT_RE = /\b(?:scale|scaled|n\.?t\.?s\.?)\b/i;
const DIMENSION_KINDS = new Set([
  'linear dimension',
  'aligned dimension',
  'angular dimension',
  'radial dimension',
  'diameter dimension',
  'elevation dimension',
  'chain dimension',
  'spot elevation',
]);

export function polylineLength(points) {
  if (!Array.isArray(points) || points.length < 2) return 0;
  let len = 0;
  for (let i = 1; i < points.length; i += 1) {
    const [x0, y0] = points[i - 1];
    const [x1, y1] = points[i];
    len += Math.hypot(x1 - x0, y1 - y0);
  }
  return len;
}

function entityLength(entity) {
  if (!entity) return 0;
  if (Array.isArray(entity.points) && entity.points.length >= 2) {
    return polylineLength(entity.points);
  }
  if (Array.isArray(entity.bbox) && entity.bbox.length >= 4) {
    return Math.max(entity.bbox[2], entity.bbox[3]);
  }
  return 0;
}

/**
 * Parse drawing scale ratio from text ("1:100", "SCALE 1 = 50").
 * @returns {{ numerator: number, denominator: number, label: string } | null}
 */
export function parseScaleRatio(text) {
  const raw = String(text || '').trim();
  if (!raw || NTS_RE.test(raw)) return null;
  const m = raw.match(SCALE_RATIO_RE);
  if (m) {
    const numerator = Number(m[1]);
    const denominator = Number(m[2]);
    if (numerator && denominator > 0) {
      return { numerator, denominator, label: `${numerator}:${denominator}` };
    }
  }
  return parseArchitecturalScaleNotation(raw);
}

/**
 * Architectural title-block scales, e.g. 1/8" = 1'-0", 3/32" = 1'-0"
 */
export function parseArchitecturalScaleNotation(text) {
  const raw = String(text || '').trim();
  if (!raw || NTS_RE.test(raw)) return null;

  const arch = raw.match(
    /(\d+)\s*\/\s*(\d+)\s*["″]?\s*=\s*(\d+)\s*['′](?:\s*-?\s*(\d+(?:\s+\d+\/\d+)?)?\s*["″]?)?/i,
  );
  if (arch) {
    const paperInches = Number(arch[1]) / Number(arch[2]);
    const realFeet = Number(arch[3]) + (arch[4] ? parseFractionalInches(arch[4]) / 12 : 0);
    if (paperInches > 0 && realFeet > 0) {
      const realInches = realFeet * 12;
      const denominator = Math.round(realInches / paperInches);
      if (denominator > 0) {
        return { numerator: 1, denominator, label: raw };
      }
    }
  }

  const metric = raw.match(/(\d+)\s*mm\s*=\s*(\d+)\s*m/i);
  if (metric) {
    const mm = Number(metric[1]);
    const meters = Number(metric[2]);
    if (mm > 0 && meters > 0) {
      const denominator = Math.round((meters * 1000) / mm);
      return { numerator: 1, denominator, label: raw };
    }
  }

  return null;
}

function textLooksLikeScale(text) {
  const raw = String(text || '').trim();
  if (!raw) return false;
  if (NTS_RE.test(raw)) return true;
  if (SCALE_RATIO_RE.test(raw)) return true;
  if (/(\d+)\s*\/\s*(\d+)\s*["″]?\s*=\s*\d+\s*['′]/i.test(raw)) return true;
  return SCALE_HINT_RE.test(raw) && raw.length < 80;
}

/**
 * Real-world inches per PDF point at a drawing scale ratio.
 * PDF points are 1/72 inch on paper; ratio multiplies paper inches to real inches.
 */
export function realInchesPerPdfPoint(ratio) {
  if (!ratio?.denominator) return null;
  return (ratio.denominator / (ratio.numerator || 1)) / 72;
}

export function pdfPointsToRealFeet(pdfPoints, ratio) {
  const inchesPerPt = realInchesPerPdfPoint(ratio);
  if (!inchesPerPt) return null;
  return (pdfPoints * inchesPerPt) / 12;
}

/** PDF-point² area → real square feet at a drawing scale ratio. */
export function pdfAreaToRealSqFeet(areaSqPt, ratio) {
  const inchesPerPt = realInchesPerPdfPoint(ratio);
  if (!inchesPerPt || areaSqPt == null) return null;
  return (areaSqPt * inchesPerPt * inchesPerPt) / 144;
}

export function formatFeetInches(totalFeet, { precision = 1 } = {}) {
  if (totalFeet == null || Number.isNaN(totalFeet)) return '—';
  const sign = totalFeet < 0 ? '-' : '';
  const abs = Math.abs(totalFeet);
  const feet = Math.floor(abs);
  const inches = (abs - feet) * 12;
  if (feet === 0) return `${sign}${inches.toFixed(precision)}"`;
  if (inches < 0.05) return `${sign}${feet}'`;
  return `${sign}${feet}'-${inches.toFixed(precision)}"`;
}

export function formatAreaSqFt(sqFt) {
  if (sqFt == null || Number.isNaN(sqFt)) return '—';
  return `${sqFt.toLocaleString(undefined, { maximumFractionDigits: 1 })} SF`;
}

/**
 * Parse architectural dimension strings from IR value/label.
 * @returns {{ feet: number, raw: string, kind: 'length' | 'area' | 'text' } | null}
 */
export function parseArchitecturalDimension(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;

  const area = raw.match(AREA_SF_RE);
  if (area) {
    return { feet: null, squareFeet: Number(area[1]), raw, kind: 'area' };
  }

  const arch = raw.match(ARCH_DIM_RE);
  if (arch) {
    const feet = Number(arch[1]) || 0;
    const inchPart = arch[2] ? parseFractionalInches(arch[2]) : 0;
    return { feet: feet + inchPart / 12, raw, kind: 'length' };
  }

  const feetOnly = raw.match(FEET_ONLY_RE);
  if (feetOnly) {
    return { feet: Number(feetOnly[1]), raw, kind: 'length' };
  }

  const inchOnly = raw.match(INCH_ONLY_RE);
  if (inchOnly) {
    return { feet: parseFractionalInches(inchOnly[1]) / 12, raw, kind: 'length' };
  }

  return { feet: null, raw, kind: 'text' };
}

function parseFractionalInches(s) {
  const parts = String(s).trim().split(/\s+/);
  let total = 0;
  for (const part of parts) {
    if (part.includes('/')) {
      const [num, den] = part.split('/').map(Number);
      if (den) total += num / den;
    } else {
      total += Number(part) || 0;
    }
  }
  return total;
}

function resolveEntitiesMap(entities) {
  const map = new Map();
  for (const ent of entities || []) {
    if (ent?.id) map.set(ent.id, ent);
  }
  return map;
}

function componentBarLength(comp, entityMap) {
  for (const id of comp.entityIds || []) {
    const ent = entityMap.get(id);
    if (!ent || ent.type === 'text') continue;
    const len = entityLength(ent);
    if (len > 0) return len;
  }
  if (Array.isArray(comp.bbox) && comp.bbox.length >= 4) {
    return Math.max(comp.bbox[2], comp.bbox[3]);
  }
  return 0;
}

function scoreScaleEntry(entry) {
  const rank = { high: 3, medium: 2, low: 1 };
  return rank[entry.confidence] || 0;
}

function buildScaleEntry({ comp, ratio, barPts, source, label }) {
  const entry = {
    componentId: comp?.id,
    pageIndex: comp?.pageIndex ?? 0,
    label: label || comp?.label || comp?.value || '',
    ratio,
    barLengthPdfPoints: barPts || 0,
    source,
    confidence: ratio ? 'medium' : 'low',
  };
  if (ratio && barPts > 0) entry.confidence = 'high';
  else if (ratio) entry.confidence = 'medium';
  return entry;
}

/**
 * Best-effort sheet scale from components + optional page entities.
 */
export function deriveSheetScale(components, entities = [], pageIndex = null) {
  const entityMap = resolveEntitiesMap(entities);
  const scales = [];

  const consider = (comp, labelText, source, barPts = 0) => {
    const ratio = parseScaleRatio(labelText);
    if (!ratio && !textLooksLikeScale(labelText)) return;
    scales.push(buildScaleEntry({
      comp,
      ratio,
      barPts,
      source,
      label: labelText,
    }));
  };

  for (const comp of components || []) {
    const kindLc = (comp.kind || '').toLowerCase();
    if (pageIndex != null && comp.pageIndex != null && comp.pageIndex !== pageIndex) continue;

    const labelText = comp.label || comp.value || '';
    if (SCALE_KINDS.has(kindLc)) {
      consider(comp, labelText, kindLc.replace(/\s+/g, '_'), componentBarLength(comp, entityMap));
      continue;
    }
    if (textLooksLikeScale(labelText)) {
      consider(comp, labelText, 'component_text');
    }
  }

  for (const ent of entities || []) {
    if (ent.type !== 'text') continue;
    const text = ent.text || '';
    if (!textLooksLikeScale(text)) continue;
    const ratio = parseScaleRatio(text);
    if (ratio) {
      scales.push({
        componentId: ent.id,
        pageIndex: ent.pageIndex ?? pageIndex ?? 0,
        label: text,
        ratio,
        barLengthPdfPoints: 0,
        source: 'page_text',
        confidence: 'medium',
      });
    }
  }

  if (!scales.length) return null;
  scales.sort((a, b) => scoreScaleEntry(b) - scoreScaleEntry(a));
  return scales[0];
}

/** PDF-space length and area for IR or user-markup geometry. */
export function measureGeometryPdf(geometry) {
  const geom = geometry || {};
  const type = geom.type || 'polyline';

  if (type === 'circle' && geom.radius > 0) {
    return {
      lengthPt: 2 * Math.PI * geom.radius,
      areaSqPt: Math.PI * geom.radius * geom.radius,
    };
  }

  if (type === 'bbox' && Array.isArray(geom.bbox) && geom.bbox.length >= 4) {
    const [, , w, h] = geom.bbox;
    return { lengthPt: 2 * (w + h), areaSqPt: w * h };
  }

  const pts = geom.points || [];
  if (pts.length < 2) {
    return { lengthPt: 0, areaSqPt: null };
  }

  const lengthPt = polylineLength(pts);
  let areaSqPt = null;
  const closed = type === 'polygon' && (geom.closed || pts.length >= 3);
  if (closed && pts.length >= 3) {
    let sum = 0;
    for (let i = 0; i < pts.length; i += 1) {
      const [x0, y0] = pts[i];
      const [x1, y1] = pts[(i + 1) % pts.length];
      sum += x0 * y1 - x1 * y0;
    }
    areaSqPt = Math.abs(sum) / 2;
  }

  return { lengthPt, areaSqPt };
}

/**
 * Measurements for inspector / scale panel.
 */
export function computeComponentMeasurements(component, { ir, entities, sheetScale } = {}) {
  if (!component) return null;

  const rows = [];
  const text = component.value || component.label || '';
  const parsed = parseArchitecturalDimension(text);
  const kindLc = (component.kind || '').toLowerCase();

  if (DIMENSION_KINDS.has(kindLc) && parsed?.kind === 'length' && parsed.feet != null) {
    rows.push({ label: 'Dimension', value: parsed.raw });
    rows.push({ label: 'Length', value: formatFeetInches(parsed.feet) });
  } else if (parsed?.kind === 'area' && parsed.squareFeet != null) {
    rows.push({ label: 'Area (labeled)', value: formatAreaSqFt(parsed.squareFeet) });
  } else if (text) {
    rows.push({ label: 'Display value', value: text });
  }

  const bbox = component.bbox;
  if (Array.isArray(bbox) && bbox.length >= 4) {
    const wPt = bbox[2];
    const hPt = bbox[3];
    rows.push({ label: 'BBox width (PDF pt)', value: `${wPt.toFixed(1)} pt` });
    rows.push({ label: 'BBox height (PDF pt)', value: `${hPt.toFixed(1)} pt` });

    if (sheetScale?.ratio) {
      const wFt = pdfPointsToRealFeet(wPt, sheetScale.ratio);
      const hFt = pdfPointsToRealFeet(hPt, sheetScale.ratio);
      if (wFt != null) rows.push({ label: 'BBox width (scaled)', value: formatFeetInches(wFt) });
      if (hFt != null) rows.push({ label: 'BBox height (scaled)', value: formatFeetInches(hFt) });
    }
  }

  if (kindLc === BAR_SCALE_KIND || SCALE_KINDS.has(kindLc)) {
    const ratio = parseScaleRatio(text);
    if (ratio) {
      rows.push({ label: 'Scale ratio', value: ratio.label });
    } else if (text) {
      rows.push({ label: 'Scale note', value: text });
    }
  }

  if (ir?.pageSize?.width && ir?.pageSize?.height) {
    rows.push({
      label: 'Page size',
      value: `${Math.round(ir.pageSize.width)} × ${Math.round(ir.pageSize.height)} pt`,
    });
  }

  const geom = component.geometry;
  if (geom && sheetScale?.ratio) {
    const { lengthPt, areaSqPt } = measureGeometryPdf(geom);
    if (lengthPt > 0) {
      const lenFt = pdfPointsToRealFeet(lengthPt, sheetScale.ratio);
      if (lenFt != null) {
        rows.push({ label: 'Geometry length', value: formatFeetInches(lenFt) });
      }
    }
    if (areaSqPt != null && areaSqPt > 0) {
      const areaSf = pdfAreaToRealSqFeet(areaSqPt, sheetScale.ratio);
      if (areaSf != null) {
        rows.push({ label: 'Geometry area', value: formatAreaSqFt(areaSf) });
      }
    }
  }

  return rows.length ? rows : null;
}

export function formatViewportReadout({ pageNum, numPages, zoomPercent, pageSize }) {
  const parts = [];
  if (numPages > 1) parts.push(`Page ${pageNum}/${numPages}`);
  if (zoomPercent) parts.push(`Zoom ${zoomPercent}%`);
  if (pageSize?.width) {
    parts.push(`Sheet ${Math.round(pageSize.width)}×${Math.round(pageSize.height)} pt`);
  }
  return parts.join(' · ') || '—';
}
